/**
 * orval 自定义 fetch mutator。
 *
 * 生成的每一个 endpoint 都不会自己调 fetch,而是调这里的 customFetch,
 * 因此 CSRF / credentials / 错误类型 / 401 语义只有这一处实现。
 *
 * ★ `ApiError` 是**全站唯一的错误身份**:应用侧 `apps/crm/src/lib/api.ts` 只 re-export 本文件
 *   这一个类(它自己的 `apiFetch` 也抛这一个)。于是「生成 client 抛的错」与「手写 fetch 抛的错」
 *   `instanceof` 恒等,表单基座按身份判定的字段级回显不再需要任何归一适配层。
 *   包不能反向依赖应用,所以真源在这里、应用侧只做 re-export ——
 *   改规则(cookie 名 / header 名 / 401 行为 / 错误形状)只有这一处。
 */

export const CSRF_COOKIE_NAME = 'csrftoken'
export const CSRF_HEADER_NAME = 'X-CSRFToken'
export const DEFAULT_BASE_URL = '' // schema 路径已含 /api/v1 完整前缀(spectacular 导出),此处不再拼接

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

/** DRF 错误响应的常见形状:{detail} / {code} / {field: [msg]} / {nested: {field: [msg]}} 混合。 */
export interface ApiErrorBody {
  detail?: string
  code?: string
  non_field_errors?: string[]
  [field: string]: unknown
}

/**
 * 所有非 2xx 与网络失败统一抛这个类型(全站唯一身份)。
 * status === 0 表示请求根本没打通(后端没起 / 网络断 / 被 abort 之外的失败)。
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly body: ApiErrorBody | undefined
  readonly url: string

  constructor(params: {
    message: string
    status: number
    url: string
    code?: string | undefined
    body?: ApiErrorBody | undefined
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.url = params.url
    this.code = params.code
    this.body = params.body
  }

  /**
   * 后端返回的**平铺**字段级错误,形如 `{ username: ['该字段是必填项。'] }`。
   * 嵌套子表单错误(`{new_customer: {name: […]}}`)不在这里 —— 拍平是表单基座的职责
   * (`apps/crm/src/components/form/api-errors.ts`),它按表单字段面决定拍成什么键。
   */
  get fieldErrors(): Record<string, string[]> {
    const out: Record<string, string[]> = {}
    if (!this.body) return out
    for (const [key, value] of Object.entries(this.body)) {
      if (key === 'detail' || key === 'code') continue
      if (Array.isArray(value) && value.every((v) => typeof v === 'string')) out[key] = value as string[]
      else if (typeof value === 'string') out[key] = [value]
    }
    return out
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  /** 4xx 属于「请求本身有问题」,重试无意义。 */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export interface ApiClientConfig {
  /** 生成代码里的相对路径会拼在这个前缀后面。 */
  baseUrl: string
  /** 401 回调:应用注入 router 跳转(默认整页跳 /login?next=)。 */
  onUnauthorized: (next: string) => void
}

const config: ApiClientConfig = {
  baseUrl: DEFAULT_BASE_URL,
  onUnauthorized: (next) => {
    if (typeof window === 'undefined') return
    if (window.location.pathname === '/login') return
    window.location.assign(`/login?next=${encodeURIComponent(next)}`)
  },
}

/** 应用启动时调用一次,把 401 行为接到 router 上。 */
export function configureApiClient(patch: Partial<ApiClientConfig>): void {
  Object.assign(config, patch)
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const raw of document.cookie.split(';')) {
    const chunk = raw.trim()
    if (chunk.startsWith(prefix)) return decodeURIComponent(chunk.slice(prefix.length))
  }
  return null
}

function currentInternalHref(): string {
  if (typeof window === 'undefined') return '/'
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function resolveUrl(url: string): string {
  if (/^https?:\/\//.test(url)) return url
  if (url.startsWith('/api/')) return url
  return `${config.baseUrl}${url.startsWith('/') ? url : `/${url}`}`
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      return (await response.json()) as unknown
    } catch {
      return undefined
    }
  }
  const text = await response.text()
  return text.length > 0 ? text : undefined
}

function messageFromBody(body: unknown, status: number): string {
  if (typeof body === 'string' && body.trim().length > 0) return body.trim().slice(0, 300)
  if (body && typeof body === 'object') {
    const record = body as ApiErrorBody
    if (typeof record.detail === 'string') return record.detail
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
      if (typeof value === 'string') return value
    }
  }
  if (status === 0) return '无法连接后端服务'
  if (status === 401) return '登录已失效,请重新登录'
  if (status === 403) return '没有权限执行该操作'
  if (status === 404) return '请求的资源不存在'
  return `请求失败(HTTP ${status})`
}

/** 成功响应的状态码 + 已解析的 body。 */
export interface ApiResponse<T> {
  /** HTTP 状态码;少数端点把结果语义放在这里(如 201 新建 / 200 幂等命中既有资源)。 */
  readonly status: number
  readonly body: T
}

/**
 * 带状态码的请求(与 `customFetch` **完全同一套**语义:CSRF / credentials / 401 / ApiError,
 * 只是成功路径上把状态码一起还给调用方)。
 *
 * 用途:后端在个别端点上用状态码区分结果 —— `POST /leads` 的 201=新建 / 200=命中进线幂等,
 * 两种结果的响应体同形,状态码是唯一判据。这类调用点从前只能自己再发一次 fetch
 * (于是 CSRF / 401 / 错误文案被抄第三遍),现在直接用这个。
 */
export async function customFetchWithStatus<T>(url: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
  const resolved = resolveUrl(url)
  const method = (init.method ?? 'GET').toUpperCase()

  const headers = new Headers(init.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  headers.set('X-Requested-With', 'XMLHttpRequest')
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE_NAME)
    if (token) headers.set(CSRF_HEADER_NAME, token)
  }

  let response: Response
  try {
    response = await fetch(resolved, { ...init, method, headers, credentials: 'same-origin' })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError({
      message: '无法连接后端服务',
      status: 0,
      url: resolved,
      code: 'network_error',
    })
  }

  const body = await parseBody(response)

  if (!response.ok) {
    const errorBody = body && typeof body === 'object' ? (body as ApiErrorBody) : undefined
    const error = new ApiError({
      message: messageFromBody(body, response.status),
      status: response.status,
      url: resolved,
      code: typeof errorBody?.code === 'string' ? errorBody.code : undefined,
      body: errorBody,
    })
    if (error.isUnauthorized) config.onUnauthorized(currentInternalHref())
    throw error
  }

  return { status: response.status, body: body as T }
}

/**
 * orval `httpClient: 'fetch'` 约定的 mutator 签名(生成物调的就是它)。
 * 生成代码调用形如:customFetch<LeadList>(`/leads${qs}`, { method: 'GET', signal })
 */
export async function customFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  return (await customFetchWithStatus<T>(url, init)).body
}

export default customFetch
